import { useQueryClient, useMutation } from "@tanstack/react-query";
import { journeyQueryKeys } from "pages/SingleJourney/queries/keys";
import JourneyTestCaseService from "../services";
import { TEST_CASE_STATUS } from "../constants";

export const invalidateJourneyTestCaseQueries = (queryClient, journeyId) => {
    queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
            if (!Array.isArray(queryKey)) {
                return false;
            }

            if (queryKey[0] !== journeyQueryKeys.journeyTestCases) {
                return false;
            }

            if (!journeyId) {
                return true;
            }

            return queryKey.includes(journeyId);
        },
    });
};

export function useDeleteTestCaseMutation({
    journeyId,
    idsToDelete,
    onSuccess,
    onError,
}) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () =>
            JourneyTestCaseService.deleteTestCases({ idsToDelete }),
        onSuccess: (data) => {
            invalidateJourneyTestCaseQueries(queryClient, journeyId);
            onSuccess?.(data);
        },
        onError: (error) => {
            onError?.(error);
        },
    });
}

export function useRegenerateTestCaseMutation(journeyId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ idsToRegenerate, regenerateAll, contextData = null }) =>
            JourneyTestCaseService.regenerateTestCases({
                journeyId,
                idsToRegenerate,
                regenerateAll,
                contextData,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [journeyQueryKeys.singleJourney, journeyId],
            });
            invalidateJourneyTestCaseQueries(queryClient, journeyId);
        },
    });
}

// TODO: remove on endpoint ready
// eslint-disable-next-line no-unused-vars
export function useRunTestCasesMutation(journeyId) {
    const queryClient = useQueryClient();
    const patchJourneyTestCases = (updater, snapshot) => {
        const queries = queryClient.getQueryCache().findAll({
            predicate: ({ queryKey }) =>
                Array.isArray(queryKey) &&
                queryKey[0] === journeyQueryKeys.journeyTestCases &&
                (!journeyId || queryKey.includes(journeyId)),
        });

        queries.forEach(({ queryKey }) => {
            const prev = queryClient.getQueryData(queryKey);
            if (snapshot && Array.isArray(snapshot)) {
                snapshot.push({ queryKey, prev });
            }
            if (!prev) return;
            const next = updater(prev);
            queryClient.setQueryData(queryKey, next);
        });
    };

    return useMutation({
        mutationFn: (idsToRun) =>
            JourneyTestCaseService.runTestCases({ journeyId, idsToRun }),
        onMutate: async (idsToRun = []) => {
            await queryClient.cancelQueries({
                predicate: ({ queryKey }) =>
                    Array.isArray(queryKey) &&
                    queryKey[0] === journeyQueryKeys.journeyTestCases &&
                    (!journeyId || queryKey.includes(journeyId)),
            });

            const snapshot = [];

            patchJourneyTestCases((current) => {
                if (!current?.testCases) return current;
                const idsSet = new Set(idsToRun);
                return {
                    ...current,
                    testCases: current.testCases.map((tc) =>
                        idsSet.has(tc.id)
                            ? {
                                  ...tc,
                                  status:
                                      tc.status === TEST_CASE_STATUS.running
                                          ? TEST_CASE_STATUS.running
                                          : TEST_CASE_STATUS.pending,
                              }
                            : tc
                    ),
                };
            }, snapshot);

            return { snapshot };
        },
        onError: (_error, _variables, context) => {
            if (!context?.snapshot) return;
            context.snapshot.forEach(({ queryKey, prev }) => {
                queryClient.setQueryData(queryKey, prev);
            });
        },
    });
}

// TODO: remove on endpoint ready
// eslint-disable-next-line no-unused-vars
export function useStopTestCasesMutation(journeyId) {
    const queryClient = useQueryClient();
    return useMutation({
        // eslint-disable-next-line no-unused-vars
        mutationFn: (idToStop) => {
            return JourneyTestCaseService.stopTestCases({
                journeyId,
                idToStop,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [journeyQueryKeys.singleJourney, journeyId],
            });
            invalidateJourneyTestCaseQueries(queryClient, journeyId);
        },
    });
}

export const useGenerateTestCasesMutation = (journeyId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (contextData) => JourneyTestCaseService.generateTestCases({journeyId, contextData}),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [journeyQueryKeys.singleJourney, journeyId],
            });
            invalidateJourneyTestCaseQueries(queryClient, journeyId);
        },
    });
};

export function useUpdateTestCaseFeedbackMutation(journeyId) {
    const queryClient = useQueryClient();

    const getNextFeedbackStats = (testCase, nextValue) => {
        const previousValue = testCase?.my_feedback ?? null;
        let likesCount = Number(testCase?.likes_count) || 0;
        let dislikesCount = Number(testCase?.dislikes_count) || 0;

        if (previousValue === 1) {
            likesCount = Math.max(0, likesCount - 1);
        }
        if (previousValue === -1) {
            dislikesCount = Math.max(0, dislikesCount - 1);
        }

        if (nextValue === 1) {
            likesCount += 1;
        }
        if (nextValue === -1) {
            dislikesCount += 1;
        }

        return {
            likes_count: likesCount,
            dislikes_count: dislikesCount,
            my_feedback: nextValue,
        };
    };

    return useMutation({
        mutationFn: ({ testCaseId, value }) =>
            JourneyTestCaseService.updateFeedback({ testCaseId, value }),
        onMutate: async ({ testCaseId, value }) => {
            await queryClient.cancelQueries({
                predicate: ({ queryKey }) =>
                    Array.isArray(queryKey) &&
                    queryKey[0] === journeyQueryKeys.journeyTestCases &&
                    (!journeyId || queryKey.includes(journeyId)),
            });

            const snapshot = [];
            const queries = queryClient.getQueryCache().findAll({
                predicate: ({ queryKey }) =>
                    Array.isArray(queryKey) &&
                    queryKey[0] === journeyQueryKeys.journeyTestCases &&
                    (!journeyId || queryKey.includes(journeyId)),
            });

            queries.forEach(({ queryKey }) => {
                const prev = queryClient.getQueryData(queryKey);
                snapshot.push({ queryKey, prev });

                if (!prev?.testCases) {
                    return;
                }

                queryClient.setQueryData(queryKey, {
                    ...prev,
                    testCases: prev.testCases.map((testCase) =>
                        testCase.id === testCaseId
                            ? {
                                  ...testCase,
                                  ...getNextFeedbackStats(testCase, value),
                              }
                            : testCase
                    ),
                });
            });

            return { snapshot };
        },
        onSuccess: (data, variables) => {
            const queries = queryClient.getQueryCache().findAll({
                predicate: ({ queryKey }) =>
                    Array.isArray(queryKey) &&
                    queryKey[0] === journeyQueryKeys.journeyTestCases &&
                    (!journeyId || queryKey.includes(journeyId)),
            });

            queries.forEach(({ queryKey }) => {
                const prev = queryClient.getQueryData(queryKey);
                if (!prev?.testCases) {
                    return;
                }

                queryClient.setQueryData(queryKey, {
                    ...prev,
                    testCases: prev.testCases.map((testCase) =>
                        testCase.id === variables.testCaseId
                            ? {
                                  ...testCase,
                                  my_feedback: data?.my_feedback ?? null,
                                  likes_count: Number(data?.likes_count) || 0,
                                  dislikes_count:
                                      Number(data?.dislikes_count) || 0,
                              }
                            : testCase
                    ),
                });
            });
        },
        onError: (_error, _variables, context) => {
            context?.snapshot?.forEach(({ queryKey, prev }) => {
                queryClient.setQueryData(queryKey, prev);
            });
        },
        onSettled: () => {
            invalidateJourneyTestCaseQueries(queryClient, journeyId);
        },
    });
}
